import {
  Component, Input, Output, EventEmitter, ViewChild, OnInit, ChangeDetectorRef,
  OnDestroy, ElementRef, ChangeDetectionStrategy, OnChanges, SimpleChanges, HostBinding, HostListener, Renderer2,
} from '@angular/core';
import {PopupComponent, Align, Collision} from '@progress/kendo-angular-popup';
import * as _ from 'lodash';
import {Offset} from '../interfaces/offset';
import {RtlService} from '../../intl/services/rtl.service';

export type TooltipDirection = 'top' | 'bottom'| 'left' | 'right';
type ArrowClass = 'top-arrow' | 'bottom-arrow' | 'left-arrow' | 'right-arrow';

@Component({
  selector: 'app-tooltip',
  templateUrl: './tooltip.component.html',
  styleUrls: ['./tooltip.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TooltipComponent implements OnInit, OnChanges, OnDestroy {
  @Input() label: string;
  @Input() anchor: HTMLElement;
  @Input() direction: TooltipDirection;
  @Input() isReady = true;
  @Input() delay = 400;
  @Input() keepOpenWhenMouseIn: boolean;
  @Input() openOnClick = false;
  @Input() openOnClickWithoutStopPropagation = false;
  @Input() width: string;
  @Input() height: string;

  @Input() disposable = true;

  // If no anchor is provided, the Popup uses the offset property value.
  @Input() offset: Offset;
  @Input() forceOpen: boolean;
  @Input() contentPadding = 5;
  @Input() tooltipPadding = true;
  @Input() alignCenter = true;
  @Input() disableClickOut = false;
  @Input() closeOnClick = false;

  @Output() close = new EventEmitter();
  @Output() open = new EventEmitter();
  @Output() dispose = new EventEmitter();

  @ViewChild('popup', {static: true}) popup: PopupComponent;
  @ViewChild('popup', {read: ElementRef, static: false}) popupElement: ElementRef;

  public tooltipPaddingPx: number;

  public anchorAlign: Align;
  public popupAlign: Align;
  public arrowClass: ArrowClass;
  public collision: Collision;

  public isTooltipOpen: boolean;
  public isMouseLeave: boolean;
  public isMouseInPopup: boolean;
  public isFirstOpen: boolean;
  public checkedPosition: boolean;
  public tooltipID: string;


  public ARROW_WIDTH = 8;

  private right = 'right';
  private left = 'left';
  private rtl: boolean;

  constructor(private changeDetector: ChangeDetectorRef,
              private rtlService: RtlService,
              private renderer: Renderer2) {
  }

  @HostBinding('class.overlay') get overlay() {
    return !this.disposable;
  }

  @HostListener('click', ['$event'])
  public onHostClick($event) {
    if (!this.disposable && $event.target.tagName.toLowerCase() === 'app-tooltip') {
      $event.stopPropagation();
      this.dispose.emit();
    }
  }

  public ngOnInit(): void {
    this.rtl = this.rtlService.isRtl();

    if (this.rtl) {
      this.right = 'left';
      this.left = 'right';
    }

    this.tooltipPaddingPx = this.tooltipPadding ? 5 : 0;
    this.collision = {vertical: 'fit', horizontal: 'fit'};
    this.setPopupAndAnchorAlignByDirection(this.direction);
    this.setArrowClassByDirection(this.direction);

    if (this.anchor && !this.forceOpen) {
      this.addAnchorEvents();
    } else {
      this.isTooltipOpen = true;
    }

    this.isFirstOpen = true;
    this.checkedPosition = false;
    this.tooltipID = 'tooltip' + _.uniqueId();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.isReady && !changes.isReady.isFirstChange() && this.isTooltipOpen &&
      changes.isReady.currentValue && !changes.isReady.previousValue) {
      setTimeout(() => {
        if (this.haveCollision()) {
          this.flip();
        }
        this.setArrowPosition();
        this.checkedPosition = true;
        this.changeDetector.markForCheck();
      }, 0);
    }
  }

  public ngOnDestroy(): void {
    this.removeAnchorEvents();
  }

  public closeTooltip() {
    this.isTooltipOpen = false;
    this.changeDetector.markForCheck();
  }

  public openTooltip() {
    this.isTooltipOpen = true;
    this.changeDetector.markForCheck();
  }

  public setArrowClassByDirection(direction: TooltipDirection) {
    switch (direction) {
      case 'top':
        this.arrowClass = 'bottom-arrow';
        break;
      case 'right':
        this.arrowClass = 'left-arrow';
        break;
      case 'left':
        this.arrowClass = 'right-arrow';
        break;
      default:
        this.arrowClass = 'top-arrow';
    }
  }

  public setPopupAndAnchorAlignByDirection(direction: TooltipDirection) {
    const horizontalAlign = this.alignCenter ? 'center' : this.left;
    const verticalAlign = this.alignCenter ? 'center' : 'bottom';

    switch (direction) {
      case 'top':
        this.anchorAlign = {horizontal: horizontalAlign, vertical: 'top'} as Align;
        this.popupAlign = {horizontal: horizontalAlign, vertical: 'bottom'} as Align;
        break;
      case 'right':
        this.anchorAlign = {horizontal: this.right, vertical: verticalAlign} as Align;
        this.popupAlign = {horizontal: this.left, vertical: verticalAlign} as Align;
        break;
      case 'left':
        this.anchorAlign = {horizontal: this.left, vertical: verticalAlign} as Align;
        this.popupAlign = {horizontal: this.right, vertical: verticalAlign} as Align;
        break;
      default:
        this.anchorAlign = {horizontal: horizontalAlign, vertical: 'bottom'} as Align;
        this.popupAlign = {horizontal: horizontalAlign, vertical: 'top'} as Align;
    }
  }

  public setArrowPosition(recalc?: boolean) {
    if (this.popupElement) {
      let anchorCenter;
      if (this.anchor) {
        //Calculate the center of the anchor with the left padding and without the right padding
        let anchorRects = this.anchor.getClientRects()[0];
        let leftBeforeOffset;
        if (this.alignCenter){
          // it is not likely that someone would require the right/left padding adjustments
          // for a centeralized tooltip. we would prefer to avoid doing unnecessary computed style checks
          // even though there is a client-rects evalaution anyway
          leftBeforeOffset = anchorRects.left + anchorRects.width / 2;
        }else {
          let compusterStyle=window.getComputedStyle(this.anchor, null);
          let paddingRight = compusterStyle.getPropertyValue('padding-right');
          let paddingLeft = compusterStyle.getPropertyValue('padding-left');
          leftBeforeOffset = anchorRects.left + anchorRects.width / 2 + this.pixelToNumber(paddingLeft) / 2 - this.pixelToNumber(paddingRight);
        }

        anchorCenter = {
          // keep using the left even for rtl since the right does not provide the absolution correct location
          left: leftBeforeOffset + (this.offset && this.offset.left ? this.offset.left : 0),
          top: anchorRects.top + (anchorRects.height / 2) + (this.offset && this.offset.top ? this.offset.top : 0)
        };
      }else{
        anchorCenter = this.offset;
      }

      let positionDirection: string;
      let positionPixelsBefore: number;
      let positionPixelsAfter: number;
      // change the minimum edge to 0
      let edge = 0;
      let popupRects = this.popupElement.nativeElement.getClientRects()[0];
      if (this.arrowClass === "top-arrow" || this.arrowClass === "bottom-arrow") {
        positionDirection = "left"; //always use left regardless of rtl
        let popupWidth = popupRects.width - this.tooltipPaddingPx * 2;
        positionPixelsBefore = Math.max(Math.min(
          Math.ceil(anchorCenter.left - popupRects.left - this.tooltipPaddingPx - this.ARROW_WIDTH/2 - 3), popupWidth - edge),
          edge);
        positionPixelsBefore=positionPixelsBefore;
        positionPixelsAfter=positionPixelsBefore+1;
      } else {
        positionDirection = "top";
        let popupHeight =  popupRects.height - this.tooltipPaddingPx * 2;
        positionPixelsBefore = Math.max(Math.min(Math.ceil(anchorCenter.top - popupRects.top - this.tooltipPaddingPx), popupHeight - edge), edge);
        positionPixelsAfter = positionPixelsBefore;
      }

      if (recalc) {
        (this.popupElement.nativeElement.parentElement).find("style").remove();
      }

      const styleElement = this.renderer.createElement('style');
      styleElement.textContent =
        '.' + this.arrowClass + '.' + this.tooltipID + ':before{' + positionDirection + ':' + positionPixelsBefore + 'px !important;} ' +
        '.' + this.arrowClass + '.' + this.tooltipID + ':after{' + positionDirection + ':' + positionPixelsAfter + 'px !important;}';
      this.popupElement.nativeElement.parentElement.append(styleElement);
    }
  }

  public addAnchorEvents() {
    if (this.anchor && this.anchor.addEventListener) {

      if (this.openOnClick) {
        this.anchor.addEventListener('click', this.toggleToolTip);
      } else {
        this.anchor.addEventListener('mouseenter', this.onAnchorMouseEnter);
        this.anchor.addEventListener('mouseleave', this.onAnchorMouseLeave);

        if (this.closeOnClick) {
          this.anchor.addEventListener('click', this.onAnchorCloseClick);
        }
      }
    }
  }

  public removeAnchorEvents() {
    if (this.anchor && this.anchor.removeEventListener) {
      if (this.openOnClick) {
        this.anchor.removeEventListener('click', this.toggleToolTip);
      } else {
        this.anchor.removeEventListener('mouseenter', this.onAnchorMouseEnter);
        this.anchor.removeEventListener('mouseleave', this.onAnchorMouseLeave);

        if (this.closeOnClick) {
          this.anchor.removeEventListener('click', this.onAnchorCloseClick);
        }
      }
    }
  }

  public onAnchorMouseEnter = (event) => {
    this.isMouseLeave = false;
    setTimeout(() => {
      if (!this.isMouseLeave) {
        this.isTooltipOpen = true;
        this.changeDetector.markForCheck();
      }
    }, this.delay);
  };

  public onAnchorCloseClick = (event) => {
    this.onClose(event);
    this.changeDetector.markForCheck();
  };

  public toggleToolTip = (event) => {
    this.isTooltipOpen = !this.isTooltipOpen;
    this.changeDetector.markForCheck();
    if (!this.openOnClickWithoutStopPropagation){
      event.stopPropagation();
    }
  };

  public onAnchorMouseLeave = (event) => {
    setTimeout(() => { // time for the gap between the anchor and the tooltip
      this.isMouseLeave = true;

      if (!this.keepOpenWhenMouseIn || !this.isMouseInPopup) {
        this.onClose(event);
      }

      this.changeDetector.markForCheck();
    }, 50);
  };

  public onPopupMouseEnter() {
    this.isMouseInPopup = true;
  }

  public onPopupMouseLeave(event) {
    this.isMouseInPopup = false;
    if (this.isMouseLeave && this.isTooltipOpen) {
      this.onClose(event);
    }
  }

  public onClose(event) {
    this.isTooltipOpen = false;
    this.close.emit(event);
  }

  public onOpen(event) {
    if (this.isFirstOpen) {
      this.isFirstOpen = false;
      // depending on the DOM the tooltip needs to set itself on the correct location so we will wait..
      setTimeout(()=> {
        if (this.isReady) {
          this.checkedPosition=true;
          if (this.haveCollision()) {
            this.flip();
          }
          this.setArrowPosition();
          this.changeDetector.markForCheck();
        }
      });
    }

    this.open.emit(event);
  }

  public haveCollision(): boolean {
    // The collision set to be "fit" so if the popup on the edge I suppose that was a collision.
    const popupRects = this.popupElement.nativeElement.getClientRects()[0];

    switch (this.direction) {
      case 'top':
        return popupRects.top <= 0;
      case this.right:
        return Math.ceil(popupRects.left + popupRects.width) >= document.body.clientWidth;
      case this.left:
        return popupRects.left <= 0;
      default:
        return Math.ceil(popupRects.top + popupRects.height) >= document.body.clientHeight;
    }
  }

  public flip() {
    let newDirection: TooltipDirection;

    switch (this.direction) {
      case 'top':
        newDirection = 'bottom';
        break;
      case 'right':
        newDirection = 'left';
        break;
      case 'left':
        newDirection = 'right';
        break;
      default:
        newDirection = 'top';
    }

    this.setPopupAndAnchorAlignByDirection(newDirection);
    this.setArrowClassByDirection(newDirection);
  }

  public onClickOut(event) {
    if (this.openOnClick) {
      if (event.target === this.anchor) {
        return;
      }

      if (this.isTooltipOpen) {
        this.toggleToolTip(event);
      }
    }
  }

  private pixelToNumber(valueWithPx:string):number{
    return +(valueWithPx.substring(0,valueWithPx.indexOf("px")));
  }
}
